<template>
  <div
    class="video-player"
    :class="[
      $blockClass(blockClass, 'video-player'),
      {
        'video-player--block': blockStyle
      }
    ]"
  >
    <div
      v-if="overlay && paused"
      class="video-player__overlay"
      @click="play"
    >
      <icon-arrow-drop-right />
    </div>
    <video
      ref="video"
      class="video-player__video"
      :autoplay="!reducedMotion && autoplay"
      :loop="loop"
      :muted="muted"
      :poster="poster"
      :preload="videoPreloadValue(preload)"
      :playsinline="playsinline"
      :ignore-play-pause-events="ignorePlayPauseEvents"
      @pause="handlePlayPauseEvent"
      @play="handlePlayPauseEvent"
      @timeupdate="handleTimeUpdateEvent"
      @durationchange="handleDurationChangeEvent"
      @loadedmetadata="onVideoLoaded"
      @click="play"
    >
      <template v-if="sources && sources.length > 1">
        <source
          v-for="(source, index) of sources"
          :key="`source-${source.url}-${index}`"
          :src="source.url"
          :type="source.mime_type"
        >
      </template>
      <source
        v-else
        :src="src"
        type="video/mp4"
      >
    </video>

    <div
      v-if="controls"
      class="video-player__controls"
    >
      <div class="video-player__right-controls">
        <button
          class="video-player__button"
          type="button"
          @click="play"
        >
          <icon-arrow-drop-right v-if="paused" />
          <icon-pause v-else />
        </button>
        <!-- v-if="hasAudio"
          RS: removed if hasAudio condition here upon client request
        -->
        <button
          class="video-player__button"
          type="button"
          @click="toggleMute"
        >
          <icon-sound-off v-if="isMuted" />
          <icon-sound-on v-else />
        </button>
      </div>

      <span
        v-if="elapsed"
        class="text-body-xs text-body-s-desktop"
        v-text="elapsed"
      />

      <input
        ref="range"
        class="video-player__range"
        type="range"
        min="0"
        max="100"
        :style="{
          '--client-width': `${rangeWidth}px`
        }"
        :value="(currentTime / duration) * 100"
        @input="handleRangeChange"
        @change="handleRangeChange"
      >
    </div>
  </div>
</template>

<script>

/**
 * Vue: Video player (video-player)
 * -----------------------------------------------------------------------------
 * A custom video player with options.
 *
 * @param {String} src - The video file.
 * @param {String} [poster] - The poster image source.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean} controls - Enables the video controls.
 * @param {Boolean} autoplay - Starts playing the video as soon as it is ready,
 * except when reduced motion is enabled.
 * @param {Boolean} loop - Starts the video over again, every time it is
 * finished.
 * @param {Boolean} muted - Specifies that the audio output of the video should
 * be muted.
 * @param {Boolean} preload - Specifies if and how the author thinks the video
 * should be loaded when the page loads.
 * @param {Boolean} playsinline - Indicates that the video is to be played
 * within the element's playback area.
 * @param {Boolean} blockStyle - Styles the player as a block element.
 *
 */
import { mapState } from 'vuex'
import { videoPreloadValue } from '~/helpers/general'

import timings from '~/config/timings'

import IconArrowDropRight from '~icons/direction/arrow-drop/right.svg'
import IconPause from '~icons/misc/pause.svg'
import IconSoundOn from '~icons/misc/sound_on.svg'
import IconSoundOff from '~icons/misc/sound_off.svg'

export default {
  name: 'VideoPlayer',

  components: {
    IconArrowDropRight,
    IconPause,
    IconSoundOn,
    IconSoundOff,
  },

  props: {
    src: {
      type: String,
      default: '',
      required: true,
    },

    poster: {
      type: String,
      default: '',
    },

    blockClass: {
      type: String,
      default: '',
    },

    controls: {
      type: Boolean,
      default: false,
    },

    autoplay: {
      type: [Boolean, String],
      default: false,
    },

    loop: {
      type: [Boolean, String],
      default: false,
    },

    muted: {
      type: [Boolean, String],
      default: false,
    },

    preload: {
      type: [Boolean, String],
      default: false,
    },

    playsinline: {
      type: [Boolean, String],
      default: false,
    },

    blockStyle: {
      type: Boolean,
      default: false,
    },

    overlay: {
      type: Boolean,
      default: false,
    },

    sources: {
      type: Array,
      default: () => [],
    },

    ignorePlayPauseEvents: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentTime: 0,
      duration: 0,
      paused: true,
      elapsed: false,
      rangeWidth: 0,
      reducedMotion: true,
      hasAudio: false,
      isMuted: this.muted,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      screenWidth: (state) => state.index.screen.width,
    }),
  },

  watch: {

    /**
     * Watches for changes to the screen width state.
     */
    screenWidth() {
      this.setRangeStyles()
    },

    src(newSrc) {
      this.updateVideoSrc(newSrc)
    },
  },

  mounted() {
    const video = this.$refs.video

    video.addEventListener('loadeddata', () => {
      setTimeout(() => {
        this.setHasAudio()
      }, timings.quick)
    })
    this.detectReducedMotion()
    this.setRangeStyles()
  },

  methods: {
    videoPreloadValue,

    /**
     * Detects and sets the reduced motion option.
     */
    detectReducedMotion() {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },

    /**
     * Handles the screen resize event.
     */
    setRangeStyles() {
      if (!this.$refs.range) {
        return
      }

      this.rangeWidth = this.$refs.range.clientWidth
    },

    setHasAudio() {
      const video = this.$refs.video

      const hasAudio = video?.mozHasAudio || video?.webkitAudioDecodedByteCount > 0

      this.hasAudio = hasAudio
    },

    /**
     * Plays the video instance.
     */
    play() {
      if (this.ignorePlayPauseEvents) {
        return
      }

      if (this.$refs.video.paused) {
        this.$refs.video.play()
        return
      }

      this.$refs.video.pause()
    },

    /**
     * Toggles mute on or off
     */
    toggleMute() {
      const video = this.$refs.video

      // eslint-disable-next-line no-unused-expressions
      this.isMuted ? video.volume = 1 : video.volume = 0
      video.muted = !video.muted

      this.isMuted = !this.isMuted
    },

    /**
     * Handles the pause event.
     * @param {Object} event - The event payload.
     */
    handlePlayPauseEvent(event) {
      if (this.ignorePlayPauseEvents) {
        return
      }

      this.paused = event.target.paused

      /**
       * Handle side effects.
       */
      this.setElapsed()
    },

    /**
     * Handles the duration change event.
     * @param {Object} event - The event payload.
     */
    handleDurationChangeEvent(event) {
      this.duration = event.target.duration

      /**
       * Handle side effects.
       */
      this.setElapsed()
    },

    /**
     * Handles the time update event.
     * @param {Object} event - The event payload.
     */
    handleTimeUpdateEvent(event) {
      this.currentTime = event.target.currentTime

      /**
       * Handle side effects.
       */
      this.setElapsed()
      this.setRangeStyles()
    },

    /**
     * Sets the formatted elapsed time for the video.
     */
    setElapsed() {
      const seconds = this.currentTime > 0 ? this.currentTime : this.duration

      if (!seconds) {
        return
      }

      const date = new Date(seconds * 1000)

      if (date) {
        this.elapsed = date.toISOString().substring(14, 19)
      }
    },

    /**
     * Handles the range element change event.
     * - Updates the video player's current time to match.
     *
     * @param {Object} event - The event payload.
     */
    handleRangeChange(event) {
      this.$refs.video.currentTime =
        (Number(event.target.value) / 100) * this.duration
    },

    updateVideoSrc(newSrc) {
      this.newSrc = newSrc

      this.$nextTick().then(() => {
        if (this.$refs.video && newSrc) {
          this.$refs.video.load()
          this.paused = true

          if (this.autoplay && !this.reducedMotion) {
            this.$refs.video.play()
          }
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import './video-player';
</style>
